import { useState, useCallback, useRef, useMemo } from 'react';
import type { ToastType, ToastProps } from '../components/display/Toast';

export interface ToastOptions {
  type: ToastType;
  title?: string;
  message: string;
  duration?: number;
}

export interface UseToastReturn {
  toasts: ToastProps[];
  toast: (options: ToastOptions) => string;
  success: (message: string, title?: string, duration?: number) => string;
  error: (message: string, title?: string, duration?: number) => string;
  warning: (message: string, title?: string, duration?: number) => string;
  info: (message: string, title?: string, duration?: number) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

export function useToast(): UseToastReturn {
  const [toasts, setToasts] = useState<ToastProps[]>([]);
  const timeoutRefs = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
    
    // Clear timeout if it exists
    const timeoutRef = timeoutRefs.current.get(id);
    if (timeoutRef) {
      clearTimeout(timeoutRef);
      timeoutRefs.current.delete(id);
    }
  }, []);

  const dismissAll = useCallback(() => {
    setToasts([]);
    
    // Clear all timeouts
    timeoutRefs.current.forEach((timeout) => clearTimeout(timeout));
    timeoutRefs.current.clear();
  }, []);

  const toast = useCallback((options: ToastOptions) => {
    const id = Math.random().toString(36).substring(2, 9);
    const duration = options.duration ?? 5000;

    const newToast: ToastProps = {
      id,
      type: options.type,
      title: options.title,
      message: options.message,
      duration,
      onClose: dismiss,
    };

    setToasts((prev) => [...prev, newToast]);

    // Auto-dismiss after duration
    if (duration > 0) {
      const timeoutId = setTimeout(() => {
        dismiss(id);
      }, duration);
      
      timeoutRefs.current.set(id, timeoutId);
    }

    return id;
  }, [dismiss]);

  const success = useCallback((message: string, title?: string, duration?: number) => {
    return toast({ type: 'success', message, title, duration });
  }, [toast]);

  const error = useCallback((message: string, title?: string, duration?: number) => {
    return toast({ type: 'error', message, title, duration });
  }, [toast]);

  const warning = useCallback((message: string, title?: string, duration?: number) => {
    return toast({ type: 'warning', message, title, duration });
  }, [toast]);

  const info = useCallback((message: string, title?: string, duration?: number) => {
    return toast({ type: 'info', message, title, duration });
  }, [toast]);

  // Memoize the return object to prevent unnecessary re-renders in components
  // that only use the callback functions (success, error, etc.)
  return useMemo(() => ({
    toasts,
    toast,
    success,
    error,
    warning,
    info,
    dismiss,
    dismissAll,
  }), [toasts, toast, success, error, warning, info, dismiss, dismissAll]);
} 